import React from 'react';
import StockChart from '../charts/StockChart.tsx';
import TradingChart from '../charts/TradingChart.tsx';
import './ChartDemo.css';

const ChartDemo: React.FC = () => {
  return (
    <div className="main-content chart-demo-container">
      <div className="demo-header">
        <h1 className="demo-title">📈 Stock Chart Demo</h1>
        <p className="demo-subtitle">
          Lightweight Charts with Pre-marked Analysis - No Drawing Tools Needed!
        </p>
      </div>

      <div className="demo-features">
        <h2>What This Chart Can Do:</h2>
        <div className="features-grid">
          <div className="feature-card">
            <span className="feature-icon">📊</span>
            <h3>Candlestick Data</h3>
            <p>Professional OHLC candlestick charts with volume</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">📍</span>
            <h3>Your Analysis</h3>
            <p>Pre-mark support, resistance, and trend lines</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">🎯</span>
            <h3>Pattern Markers</h3>
            <p>Highlight patterns you identified (breakouts, reversals, etc.)</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">⚡</span>
            <h3>Fast & Lightweight</h3>
            <p>Optimized performance, works on all devices</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">🎨</span>
            <h3>Dark/Light Mode</h3>
            <p>Automatically adapts to your theme</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">✅</span>
            <h3>100% Legal</h3>
            <p>Apache 2.0 & MIT licensed - commercial use allowed!</p>
          </div>
        </div>
      </div>

      <div className="demo-chart-section">
        <h2>📈 TradingView-Style Chart (With Price Header):</h2>
        <TradingChart
          symbol="RELIANCE"
          exchange="NSE"
          timeframe="1D"
        />

        <h2 style={{marginTop: '3rem'}}>📊 Stock Chart (With Header):</h2>
        <StockChart
          symbol="RELIANCE"
          exchange="NSE"
          timeframe="1D"
        />
      </div>

      <div className="demo-usage">
        <h2>How It Works:</h2>
        <div className="usage-steps">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Your Scanner Finds Stocks</h3>
              <p>Momentum Scanner, Rocket Launcher, etc. identify potential trades</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>You Analyze & Mark Patterns</h3>
              <p>Identify support/resistance, trends, patterns in your backend</p>
            </div>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Chart Displays Your Insights</h3>
              <p>Users see professional charts with your analysis already marked</p>
            </div>
          </div>
        </div>
      </div>

      <div className="demo-comparison">
        <h2>What Users Get vs. Don't Get:</h2>
        <div className="comparison-grid">
          <div className="comparison-card yes">
            <h3>✅ What's Included:</h3>
            <ul>
              <li>Professional candlestick charts</li>
              <li>Volume indicators</li>
              <li>Multiple timeframes (1D, 1W, 1M)</li>
              <li>YOUR pre-marked analysis</li>
              <li>Support/resistance levels</li>
              <li>Trend lines</li>
              <li>Pattern markers</li>
              <li>Zoom & pan controls</li>
              <li>Crosshair for price details</li>
              <li>Responsive mobile design</li>
            </ul>
          </div>
          <div className="comparison-card no">
            <h3>❌ Not Included:</h3>
            <ul>
              <li>User drawing tools</li>
              <li>Interactive trendline drawing</li>
              <li>Fibonacci tools</li>
              <li>Custom shape drawing</li>
            </ul>
            <p className="comparison-note">
              But that's OK! Your value is in the ANALYSIS you provide, not letting users draw their own lines.
            </p>
          </div>
        </div>
      </div>

      <div className="demo-next-steps">
        <h2>Next Steps:</h2>
        <div className="next-steps-content">
          <p>To use this in your app:</p>
          <ol>
            <li>Install the library: <code>npm install lightweight-charts</code></li>
            <li>Get stock data from Indian Stock Market API (MIT licensed)</li>
            <li>Pass your analysis data to the chart component</li>
            <li>Display on screener results, stock detail pages, etc.</li>
          </ol>
          <div className="tech-stack">
            <strong>Tech Stack:</strong>
            <ul>
              <li>📦 lightweight-charts (Apache 2.0) - Charts</li>
              <li>📦 Indian Stock Market API (MIT) - NSE/BSE Data</li>
              <li>⚛️ React + TypeScript - Your existing setup</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartDemo;
